import { useEffect } from "react";
import Fuse from "fuse.js";

import {
  getUserSplitNameFromFullName,
  normalizeString,
  validateMultiFormatEmail,
} from "@utils/strings.util";
import { User, UserForDropdown } from "@app-types/common.type";
import { IPeople } from "@interfaces/people.interface";
import { OptionGroupsModel } from "../AttendeeOptions";

interface Props {
  searchQuery: string;
  options: UserForDropdown[];
  selectedOptions: (User | IPeople)[];
  setFilteredOptionGroups: (optionGroups: OptionGroupsModel) => void;
}

export function useUpdateOptionGroups({
  searchQuery,
  options,
  selectedOptions,
  setFilteredOptionGroups,
}: Props) {
  useEffect(() => {
    const selectAllOption: UserForDropdown = {
      id: "selectAllOption",
      full_name: "Select Full Team",
      title: null,
      timezone: "",
      value: "",
      label: "Select Full Team",
      email: "",
    };

    const teamMembersOptions = options.filter((opt) => opt.type === "team members");
    const peopleOptions = options.filter((opt) => opt.type === "people");

    let filteredPeople = peopleOptions;
    let filteredTeamMembers = teamMembersOptions;

    if (searchQuery.length > 0) {
      const fusePeople = new Fuse(peopleOptions, {
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.2,
        keys: ["full_name", "email"],
      });

      const fuseTeamMembers = new Fuse(teamMembersOptions, {
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.2,
        keys: ["full_name", "email"],
      });

      const parsedSearchValue = normalizeString(searchQuery);

      filteredPeople = fusePeople.search(parsedSearchValue).map(({ item }) => item);
      filteredTeamMembers = fuseTeamMembers.search(parsedSearchValue).map(({ item }) => item);
    }

    const newOptionGroups = new OptionGroupsModel();

    if (searchQuery.length === 0) {
      newOptionGroups.users.unshift(selectAllOption);
    }

    if (filteredTeamMembers.length > 0) {
      newOptionGroups.users.push(...filteredTeamMembers);
    }

    if (filteredPeople.length > 0) {
      newOptionGroups.people.push(...filteredPeople);
    }

    // add external attendee, if email is valid and not already in the list
    const externalAttendeeData = validateMultiFormatEmail(searchQuery);

    if (externalAttendeeData) {
      const externalAttendeeSplitName = getUserSplitNameFromFullName(externalAttendeeData.fullName);

      const searchEmailInOptionsIndex = options.findIndex(
        (opt) =>
          (opt.email ?? "").toLowerCase() === (externalAttendeeData.email ?? "").toLowerCase()
      );

      const searchEmailInSelectedIndex = selectedOptions.findIndex(
        (opt) =>
          (opt.email ?? "").toLowerCase() === (externalAttendeeData.email ?? "").toLowerCase()
      );

      // add external attendee, if email is valid and user is not already in the list
      if (searchEmailInOptionsIndex === -1 && searchEmailInSelectedIndex === -1) {
        const externalAttendee = {
          id: externalAttendeeData.email ?? "",
          email: externalAttendeeData.email ?? "",
          full_name: externalAttendeeData.fullName,
          first_name: externalAttendeeSplitName?.firstName,
          last_name: externalAttendeeSplitName?.lastName,
          label: externalAttendeeData.email,
          external: true,
          title: null,
          timezone: "",
        } as UserForDropdown;

        newOptionGroups.users.unshift(externalAttendee);
      }
    }

    setFilteredOptionGroups(newOptionGroups);
  }, [options, searchQuery]);
}
